// reference.ts — relatively static reference config, ported from data.jsx.
// Kept client-side for synchronous name lookups (the prototype read these
// globally). These would later move behind the API (Master Data module);
// the lookups below are the single seam to change when they do.

import type { Store, Category, BinType } from './types';

export const STORES: Store[] = [
  { id: 'ST-MAN', code: 'ST-MAN', name: 'Mansfield Rd', city: 'Nottingham' },
  { id: 'ST-CHE', code: 'ST-CHE', name: 'Chesterfield High St', city: 'Chesterfield' },
  { id: 'ST-NOT', code: 'ST-NOT', name: 'Victoria Centre', city: 'Nottingham' },
  { id: 'ST-DER', code: 'ST-DER', name: 'Derby Cornmarket', city: 'Derby' },
];

export const CATEGORIES: Category[] = [
  {
    id: 'analgesics',
    name: 'Analgesics',
    binType: 'GEN',
    fefo: 'Standard FEFO',
    color: 'var(--primary-main)',
  },
  {
    id: 'antibiotics',
    name: 'Antibiotics',
    binType: 'GEN',
    fefo: 'Standard FEFO',
    color: 'var(--info-main)',
  },
  {
    id: 'cardiovascular',
    name: 'Cardiovascular',
    binType: 'GEN',
    fefo: 'Standard FEFO',
    color: 'var(--success-main)',
  },
  {
    id: 'diabetes',
    name: 'Diabetes (Fridge)',
    binType: 'FRG',
    fefo: 'Cold-chain FEFO',
    color: 'var(--info-main)',
  },
  {
    id: 'vaccines',
    name: 'Vaccines (Fridge)',
    binType: 'FRG',
    fefo: 'Cold-chain FEFO',
    color: 'var(--info-dark)',
  },
  {
    id: 'controlled',
    name: 'Controlled Drugs',
    binType: 'CD',
    fefo: 'CD FEFO + dual sign-off',
    color: 'var(--error-main)',
  },
];

export const BIN_TYPES: BinType[] = [
  {
    id: 'GEN',
    name: 'General',
    temp: 'Ambient 15–25°C',
    desc: 'Standard shelving for ambient lines.',
  },
  { id: 'FRG', name: 'Fridge', temp: 'Cold 2–8°C', desc: 'Temperature-monitored cold storage.' },
  {
    id: 'CD',
    name: 'Controlled Drug',
    temp: 'Ambient 15–25°C',
    desc: 'BS-2881 CD cabinet, dual-locked.',
  },
  {
    id: 'RSV',
    name: 'Reserved',
    temp: 'Ambient 15–25°C',
    desc: 'Quarantine / awaiting-disposition hold.',
  },
];

export const ADJ_REASONS: string[] = [
  'Damaged in storage',
  'Stock count correction',
  'Expired write-off',
  'Dispensing error',
  'Breakage / spillage',
  'Supplier shortfall',
  'Theft / loss (CD)',
  'Returned to supplier',
];

export function catName(id: string): string {
  return CATEGORIES.find((c) => c.id === id)?.name || id;
}

// Real-store names registered at runtime (populated by api/useStores when the
// identity store list loads) so every storeName() call site — there are ~60 —
// resolves real UUIDs without converting each component to a hook.
const RUNTIME_STORES: Record<string, string> = {};

export function registerStoreNames(stores: { id: string; name: string }[]): void {
  for (const s of stores) RUNTIME_STORES[s.id] = s.name;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function storeName(id: string): string {
  if (id === 'ALL') return 'All Stores';
  const known = RUNTIME_STORES[id] ?? STORES.find((s) => s.id === id)?.name;
  if (known) return known;
  // Identifier policy: never render a bare UUID — an out-of-scope store the
  // API didn't name gets an honest label. Mock/reference ids pass through.
  return UUID_RE.test(id) ? 'Unknown store' : id;
}

export function activeStores(): Store[] {
  return STORES.filter((s) => s.active !== false);
}
